import { useState } from 'react';

interface ExchangeCalculatorProps {
  currentRate: number;
}

const ExchangeCalculator = ({ currentRate }: ExchangeCalculatorProps) => {
  const [usdAmount, setUsdAmount] = useState(100);
  const [mxnAmount, setMxnAmount] = useState(100);
  const [activeInput, setActiveInput] = useState<'usd' | 'mxn'>('usd');

  const handleUsdChange = (value: number) => {
    setUsdAmount(value);
    setMxnAmount(value * currentRate);
    setActiveInput('usd');
  };

  const handleMxnChange = (value: number) => {
    setMxnAmount(value);
    setUsdAmount(value / currentRate);
    setActiveInput('mxn');
  };

  const presetAmounts = [50, 100, 200, 500, 1000];

  return (
    <div className="bg-gradient-to-br from-slate-50 to-zinc-50 rounded-3xl p-12">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-zinc-900 mb-4">Currency Calculator</h2>
        <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
          Convert between US Dollars and Mexican Pesos with live exchange rates
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* USD Input */}
          <div className="bg-white rounded-2xl p-8 border border-zinc-200 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center">
                <span className="text-2xl font-bold text-white">$</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-zinc-900">US Dollars</h3>
                <p className="text-zinc-500">United States Dollar (USD)</p>
              </div>
            </div>
            <input
              type="number"
              value={activeInput === 'usd' ? usdAmount : usdAmount.toFixed(2)}
              onChange={(e) => handleUsdChange(parseFloat(e.target.value) || 0)}
              className="w-full text-4xl font-bold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-4 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="0.00"
            />
          </div>

          {/* MXN Input */}
          <div className="bg-white rounded-2xl p-8 border border-zinc-200 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center">
                <span className="text-2xl font-bold text-white">$</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-zinc-900">Mexican Pesos</h3>
                <p className="text-zinc-500">Mexican Peso (MXN)</p>
              </div>
            </div>
            <input
              type="number"
              value={activeInput === 'mxn' ? mxnAmount : mxnAmount.toFixed(2)}
              onChange={(e) => handleMxnChange(parseFloat(e.target.value) || 0)}
              className="w-full text-4xl font-bold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl px-6 py-4 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Exchange Rate Display */}
        <div className="text-center mt-8 mb-8">
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-8 py-4 border border-zinc-200 shadow-lg">
            <span className="text-lg text-zinc-600">Exchange Rate:</span>
            <span className="text-2xl font-bold text-emerald-600">1 USD = {currentRate.toFixed(4)} MXN</span>
          </div>
        </div>

        {/* Preset Amounts */}
        <div className="text-center">
          <p className="text-zinc-600 mb-4">Quick amounts:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => handleUsdChange(amount)}
                className="px-6 py-3 bg-emerald-100 text-emerald-700 rounded-full font-semibold hover:bg-emerald-200 transition-all whitespace-nowrap"
              >
                ${amount} USD
              </button>
            ))}
          </div>
        </div>

        {/* Conversion Examples */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="bg-blue-50 rounded-2xl p-6">
            <h4 className="text-lg font-bold text-blue-900 mb-4">Popular USD Amounts</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-blue-700">$20 USD</span>
                <span className="font-semibold text-blue-900">${(20 * currentRate).toFixed(2)} MXN</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">$50 USD</span>
                <span className="font-semibold text-blue-900">${(50 * currentRate).toFixed(2)} MXN</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-700">$100 USD</span>
                <span className="font-semibold text-blue-900">${(100 * currentRate).toFixed(2)} MXN</span>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-2xl p-6">
            <h4 className="text-lg font-bold text-green-900 mb-4">Popular MXN Amounts</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-green-700">$500 MXN</span>
                <span className="font-semibold text-green-900">${(500 / currentRate).toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">$1,000 MXN</span>
                <span className="font-semibold text-green-900">${(1000 / currentRate).toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-green-700">$2,000 MXN</span>
                <span className="font-semibold text-green-900">${(2000 / currentRate).toFixed(2)} USD</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeCalculator;